import { useMemo } from 'react';
import { PIECES } from '../../data';
import { CHORALE_IDS } from '../../data/chorales';
import { PIANO_IDS, PRELUDE_IDS, SONATA_IDS, VARIATION_IDS } from '../../data/sonatas';
import { go, useWidth } from '../hooks';
import { buildView } from '../model';
import { PlayIcon } from '../components/Icons';
import { requestPlay } from '../intent';

/** The real tension curve of BWV 846, computed by the engine, as the opening image. */
function PieceBreath({ width }: { width: number }) {
  const view = useMemo(() => buildView(PIECES.bwv846), []);
  const h = 120;
  const pad = 4;
  const x = (t: number) => pad + (t / view.length) * (width - pad * 2);
  const y = (v: number) => 8 + (1 - v) * (h - 16);
  const pts = view.tension.points;
  let d = `M${x(pts[0].t)},${y(pts[0].v)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    d += ` C${x(p1.t) + (x(p2.t) - x(p0.t)) / 6},${y(p1.v) + (y(p2.v) - y(p0.v)) / 6} ${x(p2.t) - (x(p3.t) - x(p1.t)) / 6},${y(p2.v) - (y(p3.v) - y(p1.v)) / 6} ${x(p2.t)},${y(p2.v)}`;
  }
  return (
    <svg width={width} height={h} viewBox={`0 0 ${width} ${h}`} role="img" aria-label="The tension of Bach’s Prelude in C over its 35 bars: rising and falling, highest near the end, resolving at the last bar.">
      <defs>
        <linearGradient id="home-tg" gradientUnits="userSpaceOnUse" x1="0" y1={h} x2="0" y2="0">
          <stop offset="0" className="st-rest" />
          <stop offset="1" className="st-tension" />
        </linearGradient>
      </defs>
      <path d={d} fill="none" stroke="url(#home-tg)" strokeWidth={2.6} strokeLinecap="round" />
    </svg>
  );
}

export function Home() {
  const [ref, width] = useWidth<HTMLButtonElement>();
  const total = 1 + PRELUDE_IDS.length + CHORALE_IDS.length + SONATA_IDS.length + VARIATION_IDS.length + PIANO_IDS.length;
  const listen = () => {
    requestPlay('bwv846');
    go('bwv846');
  };
  const sonata = SONATA_IDS.includes('k545-1');
  return (
    <div className="home">
      <section className="hero2">
        <div className="hero2-text">
          <span className="label">An atlas of tonal harmony</span>
          <h1>Hear how harmony pulls.</h1>
          <p className="thesis">
            Every moment of tonal music leans toward home. Press play and watch one line follow that pull: it rises when the harmony pulls away and falls when it comes
            to rest. No theory needed; the names are there when you want them.
          </p>
          <div className="hero2-actions">
            <a className="cta" href="#bwv846" onClick={(e) => { e.preventDefault(); listen(); }}>
              <span className="cta-icon" aria-hidden="true">
                <PlayIcon />
              </span>
              <span>
                <b>Listen: Bach’s Prelude in C</b>
                <small>Two minutes. Just the music and its line.</small>
              </span>
            </a>
            <a className="btn" href="#library">
              Browse {total} pieces
            </a>
          </div>
        </div>
        <button className="hero2-curve" ref={ref} onClick={listen} aria-label="Play Bach’s Prelude in C and follow its line">
          {width > 0 && <PieceBreath width={Math.round(width - 32)} />}
          <span className="hero2-caption">
            <span>The Prelude’s line, bar 1 to 35</span>
            <span className="muted">higher = pulling away, lower = at rest</span>
          </span>
        </button>
      </section>

      <section className="ways-in" aria-labelledby="ways-in">
        <h2 id="ways-in">Ways in</h2>
        <div className="ways">
          <div className="way">
            <span className="label">Compare</span>
            <h3>One melody, five harmonies</h3>
            <p>Bach harmonizes the same hymn tune five times in the St Matthew Passion. Hear one note change meaning.</p>
            <a className="way-go" href="#passion">
              Open the Passion Chorale →
            </a>
          </div>
          {VARIATION_IDS.includes('k265') && (
            <div className="way">
              <span className="label">Theme and variations</span>
              <h3>“Twinkle, Twinkle”, twelve ways</h3>
              <p>Mozart dresses a nursery tune twelve times. The harmony underneath barely moves.</p>
              <a className="way-go" href="#returns.k265">
                Hear every variation →
              </a>
            </div>
          )}
          {sonata ? (
            <div className="way">
              <span className="label">A sonata</span>
              <h3>Home through the wrong door</h3>
              <p>In Mozart’s best-known sonata the opening tune comes back in the wrong key. Hear how he still gets home.</p>
              <a className="way-go" href={`#k545-1`}>
                Listen to K. 545 →
              </a>
            </div>
          ) : (
            <div className="way">
              <span className="label">Patterns</span>
              <h3>The patterns composers used</h3>
              <p>Cadences and stock progressions heard so often they became the vocabulary of a style.</p>
              <a className="way-go" href="#paths">
                Hear the patterns →
              </a>
            </div>
          )}
          {PRELUDE_IDS.length > 0 && (
            <div className="way">
              <span className="label">A tour</span>
              <h3>Twenty-four keys in four minutes</h3>
              <p>Bach wrote a prelude in every major and minor key. Hear each opening in turn and listen for how each finds its own home.</p>
              <a className="way-go" href="#keys">
                Take the tour →
              </a>
            </div>
          )}
          <div className="way">
            <span className="label">Play</span>
            <h3>Train your ear</h3>
            <p>Short listening questions made from real music: does this phrase end at home or on a question? Or play chords yourself.</p>
            <a className="way-go" href="#train">
              Start training →
            </a>
          </div>
        </div>
      </section>

      <section className="explore" aria-labelledby="explore">
        <h2 id="explore">Explore everything</h2>
        <ul className="explore-list">
          <li>
            <a href="#library">Pieces</a>
            <span>
              {total} pieces: {PRELUDE_IDS.length ? <a href="#keys">{PRELUDE_IDS.length + 1} preludes of the Well-Tempered Clavier</a> : 'the Prelude in C'}, {CHORALE_IDS.length} Bach chorales{SONATA_IDS.length ? `, ${SONATA_IDS.length} Mozart sonata movements` : ''}
              {VARIATION_IDS.length ? ` and ${VARIATION_IDS.length} sets of variations` : ''}.
            </span>
          </li>
          <li>
            <a href="#compare">Compare</a>
            <span>The same music heard twice: one tune, many harmonies; themes and variations{SONATA_IDS.length ? '; sonata themes when they return' : ''}.</span>
          </li>
          <li>
            <a href="#paths">Patterns</a>
            <span>
              Cadences, sequences and stock progressions, <a href="#feel">or browse by what you feel</a>.
            </span>
          </li>
          <li>
            <a href="#observatory">Statistics</a>
            <span>What the composers do, counted. Every number plays the moments it counts.</span>
          </li>
          <li>
            <a href="#walk">Play</a>
            <span>
              Play chords on the gravity well, or <a href="#train">train your ear</a> with questions made from the corpus.
            </span>
          </li>
          <li>
            <a href="#method">About</a>
            <span>How to use the atlas and how every line is computed. No AI runs here.</span>
          </li>
        </ul>
      </section>

      <section className="principles" aria-label="Principles">
        <div>
          <h3>Harmony is a verb</h3>
          <p>Chords mean nothing alone. The unit here is motion: where a chord comes from, where it wants to go, and how each voice gets there.</p>
        </div>
        <div>
          <h3>Compare to understand</h3>
          <p>Change one thing and hear what it did. Remove an F♯ and a modulation vanishes; soften a chord and the anguish goes.</p>
        </div>
        <div>
          <h3>Names come last</h3>
          <p>Choose how much theory you want in the settings at the top, from none at all to full explanations.</p>
        </div>
      </section>
    </div>
  );
}
